import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getAdminSession } from "@/lib/admin.functions";
import {
  adminCreateUser,
  adminDeleteUser,
  adminListUsers,
  adminSetUserPassword,
  adminSetUserRole,
  type AdminRole,
} from "@/lib/users.functions";

export const Route = createFileRoute("/admin/users")({
  staticData: { sitemap: false },
  component: UsersPage,
});

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrator",
  editor: "Content manager",
  user: "No access",
};

function UsersPage() {
  const session = useServerFn(getAdminSession);
  const list = useServerFn(adminListUsers);
  const create = useServerFn(adminCreateUser);
  const setRole = useServerFn(adminSetUserRole);
  const setPassword = useServerFn(adminSetUserPassword);
  const remove = useServerFn(adminDeleteUser);
  const queryClient = useQueryClient();

  const { data: me } = useQuery({ queryKey: ["admin-session"], queryFn: () => session({}) });
  const { data: users, isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => list({}),
    enabled: me?.isAdmin === true,
  });

  const [newRole, setNewRole] = useState<AdminRole>("editor");
  const [passwordFor, setPasswordFor] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
  const fail = (error: Error) => toast.error(error.message);

  const createMutation = useMutation({
    mutationFn: (input: { email: string; password: string; full_name: string; role: AdminRole }) =>
      create({ data: input }),
    onSuccess: () => {
      toast.success("User created");
      refresh();
    },
    onError: fail,
  });

  const roleMutation = useMutation({
    mutationFn: (input: { user_id: string; role: AdminRole }) => setRole({ data: input }),
    onSuccess: () => {
      toast.success("Role updated");
      refresh();
    },
    onError: fail,
  });

  const passwordMutation = useMutation({
    mutationFn: (input: { user_id: string; password: string }) => setPassword({ data: input }),
    onSuccess: () => {
      toast.success("Password updated");
      setPasswordFor(null);
      setNewPassword("");
    },
    onError: fail,
  });

  const deleteMutation = useMutation({
    mutationFn: (user_id: string) => remove({ data: { user_id } }),
    onSuccess: () => {
      toast.success("User deleted");
      refresh();
    },
    onError: fail,
  });

  if (me && !me.isAdmin) {
    return (
      <>
        <h1 className="font-display text-3xl font-bold">Users</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Only administrators can manage user accounts and roles.
        </p>
      </>
    );
  }

  function onCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createMutation.mutate(
      {
        email: String(form.get("email") ?? ""),
        password: String(form.get("password") ?? ""),
        full_name: String(form.get("full_name") ?? ""),
        role: newRole,
      },
      { onSuccess: () => (event.target as HTMLFormElement).reset() },
    );
  }

  return (
    <>
      <h1 className="font-display text-3xl font-bold">Users</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Administrators manage accounts, roles and passwords. Content managers can edit content and
        pricing, but never users.
      </p>

      <form
        onSubmit={onCreate}
        className="mt-8 grid gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-2 lg:grid-cols-5 lg:items-end"
      >
        <div className="grid gap-2">
          <Label htmlFor="full_name">Name</Label>
          <Input id="full_name" name="full_name" autoComplete="off" />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" name="email" type="email" required autoComplete="off" />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="text"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="role">Role</Label>
          <Select value={newRole} onValueChange={(value) => setNewRole(value as AdminRole)}>
            <SelectTrigger id="role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="editor">Content manager</SelectItem>
              <SelectItem value="admin">Administrator</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Add user
        </Button>
      </form>

      {isLoading ? (
        <div className="mt-10 flex justify-center">
          <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
        </div>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs tracking-wide text-muted-foreground uppercase">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Last sign-in</th>
                <th className="px-4 py-3">Password</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(users ?? []).map((user) => (
                <tr key={user.id} className="border-t border-border align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium">{user.full_name || user.email}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    {user.id === me?.userId ? (
                      <Badge variant="outline" className="mt-1">
                        You
                      </Badge>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <Select
                      value={user.role === "user" ? "" : user.role}
                      onValueChange={(value) =>
                        roleMutation.mutate({ user_id: user.id, role: value as AdminRole })
                      }
                    >
                      <SelectTrigger className="w-48" aria-label={`Role for ${user.email}`}>
                        <SelectValue placeholder={ROLE_LABEL[user.role]} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">Content manager</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {user.last_sign_in_at
                      ? new Date(user.last_sign_in_at).toISOString().slice(0, 10)
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {passwordFor === user.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={newPassword}
                          onChange={(event) => setNewPassword(event.target.value)}
                          placeholder="New password"
                          aria-label={`New password for ${user.email}`}
                          className="w-44"
                        />
                        <Button
                          size="sm"
                          disabled={newPassword.length < 8 || passwordMutation.isPending}
                          onClick={() =>
                            passwordMutation.mutate({ user_id: user.id, password: newPassword })
                          }
                        >
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setPasswordFor(null)}>
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setPasswordFor(user.id);
                          setNewPassword("");
                        }}
                      >
                        Reset password
                      </Button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {user.id === me?.userId ? null : (
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Delete ${user.email}`}
                        onClick={() => {
                          if (confirm(`Delete ${user.email}?`)) deleteMutation.mutate(user.id);
                        }}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
